/**
 * Entry Card Component - Displays a single verified entry
 */

import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  PhotoLibrary as MediaIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { VerifiedEntry } from '@/types/api';
import { EntryStatus } from '@/types/enums';
import { formatDate } from '@/utils/dateUtils';
import MessageBreakdownDialog from './MessageBreakdownDialog';

interface EntryCardProps {
  entry: VerifiedEntry;
  onEdit?: (entry: VerifiedEntry) => void;
  onDelete?: (entry: VerifiedEntry) => void;
}

const EntryCard: React.FC<EntryCardProps> = ({
  entry,
  onEdit,
  onDelete,
}) => {
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const getStatusColor = (status: EntryStatus) => {
    switch (status) {
      case EntryStatus.PUBLISHED:
        return 'success';
      case EntryStatus.REVIEWED:
        return 'info';
      case EntryStatus.DRAFT:
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <Card>
      <CardContent>
        {/* Header: Status and Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Chip
            label={entry.status}
            size="small"
            color={getStatusColor(entry.status)}
          />
          <Box>
            <Tooltip title="View Message Details">
              <IconButton size="small" onClick={() => setDetailsDialogOpen(true)}>
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {onEdit && (
              <Tooltip title="Edit Entry">
                <IconButton size="small" onClick={() => onEdit(entry)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip title="Delete Entry">
                <IconButton size="small" color="error" onClick={() => onDelete(entry)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Title */}
        <Typography variant="h6" gutterBottom sx={{ direction: 'rtl', textAlign: 'right' }}>
          {entry.title}
        </Typography>

        {/* Author and MS No */}
        <Box sx={{ mb: 2 }}>
          {entry.author && (
            <Typography variant="body2" color="text.secondary">
              <strong>Author:</strong> {entry.author}
            </Typography>
          )}
          {entry.ms_no && (
            <Typography variant="body2" color="text.secondary">
              <strong>MS No:</strong> {entry.ms_no}
            </Typography>
          )}
        </Box>

        {/* Library */}
        {entry.library && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>Library:</strong> {entry.library.canonical_name} ({entry.library.slug})
          </Typography>
        )}

        {/* Media Files */}
        {entry.media_links && entry.media_links.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <MediaIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {entry.media_links.length} media file{entry.media_links.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        )}

        {/* Notes */}
        {entry.notes && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
            {entry.notes}
          </Typography>
        )}

        {/* Metadata */}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
          Created: {formatDate(entry.created_at, 'PP')} • Message #{entry.title_message_id}
        </Typography>
      </CardContent>

      {/* Message Breakdown Dialog */}
      <MessageBreakdownDialog
        open={detailsDialogOpen}
        entry={entry}
        chatId={entry.chat_id}
        onClose={() => setDetailsDialogOpen(false)}
      />
    </Card>
  );
};

export default EntryCard;
